import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Button,
  IconButton,
  Stack,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Close, PhotoCamera, Delete } from '@mui/icons-material';
import { add_goal_entry, AddEntryMedia } from 'src/auth/context/jwt/program';
import { toast } from 'sonner';

const AddEntryDialog = ({ open, onClose, onRefresh, goalData }) => {
  const [newEntry, setNewEntry] = useState({
    title: '',
    notes: '',
    value: '',
    entry_type: 'generic',
    custom_goal_milestone_id: null,
    media_ids: [],
  });

  const [uploadingMedia, setUploadingMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const isCategorical = goalData?.category?.name === 'Categorical';
  const isNumeric = !isCategorical;

  const handleSave = async () => {
    try {
      setLoading(true);

      // Filter out only successfully uploaded media IDs
      const uploadedMediaIds = uploadingMedia
        .filter((media) => media.id) // Only include items with IDs (successfully uploaded)
        .map((media) => media.id);

      // Prepare form data
      const formData = {
        custom_goal_id: goalData?.id,
        title: newEntry.title,
        notes: newEntry.notes,
        entry_type: newEntry.entry_type,
        media_ids: uploadedMediaIds.join(','), // Use uploaded media IDs
      };

      // Add milestone ID if entry type is milestone
      if (newEntry.entry_type === 'milestone' && newEntry.custom_goal_milestone_id) {
        formData.custom_goal_milestone_id = newEntry.custom_goal_milestone_id;
      }

      // Add value if goal is numeric
      if (isNumeric && newEntry.value) {
        formData.value = newEntry.value;
      }

      // Call API to save entry
      const response = await add_goal_entry(formData);

      toast.success('Entry added successfully!');

      // If successful, refresh the parent component
      if (onRefresh) {
        onRefresh();
      }

      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to add entry:', error);
      toast.error(typeof error === 'string' ? error : error?.error || 'Failed to add entry');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setNewEntry({
      title: '',
      notes: '',
      value: '',
      entry_type: 'generic',
      custom_goal_milestone_id: null,
      media_ids: [],
    });
    setUploadingMedia([]);
  };

  // Upload single file
  const Upload_Post_Media = async (file, index) => {
    try {
      const res = await AddEntryMedia({
        file,
        type: file?.type?.includes('video') ? 'video' : 'image',
      });

      const mediaData = res?.data;

      // Update the uploading item with uploaded data
      setUploadingMedia((prev) =>
        prev.map((m, i) =>
          i === index
            ? {
                ...m,
                uploading: false,
                id: mediaData?.id,
                url: mediaData?.media || mediaData?.url, // Use the URL from response
                previewUrl: m.previewUrl, // Keep the preview URL
                type: file?.type?.includes('video') ? 'video' : 'image',
                name: file.name,
              }
            : m
        )
      );

      // Update media_ids in newEntry
      if (mediaData?.id) {
        setNewEntry((prev) => ({
          ...prev,
          media_ids: [...prev.media_ids, mediaData.id].filter(
            (id, idx, arr) => arr.indexOf(id) === idx
          ),
        }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(typeof error === 'string' ? error : error?.error || 'Upload failed');

      // Remove failed upload from array
      setUploadingMedia((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Handle file selection
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Clear file input to allow re-uploading same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Step 1: Add loading placeholders for new files
    const placeholders = files.map((file) => ({
      file,
      type: file?.type,
      uploading: true,
      name: file.name,
      size: file.size,
      id: null,
      previewUrl: URL.createObjectURL(file), // Create preview URL immediately
      url: null, // Will be set after upload
    }));

    setUploadingMedia((prev) => [...prev, ...placeholders]);

    // Step 2: Upload each file sequentially
    for (let i = 0; i < files.length; i += 1) {
      const globalIndex = uploadingMedia.length + i;
      // eslint-disable-next-line no-await-in-loop
      await Upload_Post_Media(files[i], globalIndex);
    }
  };

  const removeMedia = (index) => {
    const mediaToRemove = uploadingMedia[index];

    // Revoke object URL to prevent memory leaks
    if (mediaToRemove.previewUrl) {
      URL.revokeObjectURL(mediaToRemove.previewUrl);
    }

    // Remove from uploadingMedia array
    setUploadingMedia((prev) => prev.filter((_, i) => i !== index));

    // Remove from media_ids if uploaded
    if (mediaToRemove.id) {
      setNewEntry((prev) => ({
        ...prev,
        media_ids: prev.media_ids.filter((id) => id !== mediaToRemove.id),
      }));
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const getMilestoneIdFromIndex = (index) => {
    const incompleteMilestones =
      goalData?.milestones?.filter((milestone) => !milestone.is_completed) || [];
    return incompleteMilestones[index]?.id || null;
  };

  const handleMilestoneChange = (index) => {
    const milestoneId = getMilestoneIdFromIndex(index);
    setNewEntry((prev) => ({
      ...prev,
      custom_goal_milestone_id: milestoneId,
      milestoneIndex: index,
    }));
  };

  const handleEntryTypeChange = (type) => {
    setNewEntry((prev) => ({
      ...prev,
      entry_type: type,
      custom_goal_milestone_id: null,
      milestoneIndex: null,
    }));
  };

  const isAnyMediaUploading = uploadingMedia.some((m) => m.uploading);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Entry
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Title"
            value={newEntry.title}
            onChange={(e) => setNewEntry((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Enter a title for this entry"
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Notes"
            value={newEntry.notes}
            onChange={(e) => setNewEntry((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any notes or details..."
            multiline
            rows={3}
            disabled={loading}
          />

          {isNumeric && goalData?.unit?.name && (
            <TextField
              fullWidth
              type="number"
              label="Value"
              value={newEntry.value}
              onChange={(e) => setNewEntry((prev) => ({ ...prev, value: e.target.value }))}
              placeholder={`Enter value in ${goalData.unit.name}`}
              InputProps={{
                endAdornment: (
                  <Typography variant="body2" color="text.secondary">
                    {goalData.unit.name}
                  </Typography>
                ),
              }}
              disabled={loading}
              required={isNumeric}
            />
          )}

          <FormControl component="fieldset" disabled={loading}>
            <FormLabel component="legend">Entry Type</FormLabel>
            <RadioGroup
              value={newEntry.entry_type}
              onChange={(e) => handleEntryTypeChange(e.target.value)}
            >
              <FormControlLabel value="generic" control={<Radio />} label="Generic Entry" />
              <FormControlLabel
                value="milestone"
                control={<Radio />}
                label="Mark as Milestone Achievement"
              />
            </RadioGroup>
          </FormControl>

          {newEntry.entry_type === 'milestone' && goalData?.milestones && (
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Select Milestone</InputLabel>
              <Select
                value={newEntry.milestoneIndex}
                label="Select Milestone"
                onChange={(e) => handleMilestoneChange(e.target.value)}
                required={newEntry.entry_type === 'milestone'}
              >
                <MenuItem value={null}>Select Milestone</MenuItem>
                {goalData.milestones
                  .filter((milestone) => !milestone.is_completed)
                  .map((milestone, index) => (
                    <MenuItem key={milestone.id} value={index}>
                      {isCategorical
                        ? milestone.description || `Milestone ${index + 1}`
                        : `${milestone.value} ${milestone.unit?.name || ''}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            multiple
            accept="image/*"
          />

          <Button
            startIcon={<PhotoCamera />}
            variant="outlined"
            onClick={triggerFileInput}
            disabled={loading}
            fullWidth
          >
            Add Photo
          </Button>

          {/* Uploaded media preview */}
          {uploadingMedia.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Uploaded Media:
              </Typography>
              <Stack spacing={1}>
                {uploadingMedia.map((media, index) => (
                  <Box
                    key={`${media.name}-${index}-${media.id || 'uploading'}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      gap: 1.5,
                      backgroundColor: media.uploading ? 'action.hover' : 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      {/* Media Preview - Show preview or uploaded image */}
                      {(media.previewUrl || media.url) && (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0,
                            position: 'relative',
                            backgroundColor: 'action.hover',
                          }}
                        >
                          <img
                            src={media.url || media.previewUrl} // Use uploaded URL if available, otherwise preview
                            alt={media.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              console.error('Image failed to load:', media.url || media.previewUrl);
                              e.target.style.display = 'none';
                            }}
                          />
                          {media.uploading && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.7)',
                              }}
                            >
                              <CircularProgress size={24} color="inherit" />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Media Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {media.name || `Media ${index + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {media.uploading ? 'Uploading...' : media.id ? 'Uploaded ✓' : 'Ready'}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => removeMedia(index)}
                      disabled={loading || media.uploading}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            !newEntry.title ||
            (isNumeric && newEntry.entry_type !== 'milestone' && !newEntry.value) ||
            (isNumeric && newEntry.entry_type === 'milestone' && !newEntry.value) ||
            (newEntry.entry_type === 'milestone' && !newEntry.custom_goal_milestone_id) ||
            loading ||
            isAnyMediaUploading
          }
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Adding...
            </Box>
          ) : (
            'Add Entry'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntryDialog;
